import { TRANSPARENT_HEX_COLOR } from "./../../_shared/colors.js";
const DEFAULT_COLOR = "var(--ricos-internal-table-border-color)";
const DEFAULT_WIDTH = 1;
const isTransparent = (color) => !color || color === TRANSPARENT_HEX_COLOR;
const getCornerOffset = ({
  thisZ,
  perpZ,
  parallelZ,
  extAmount,
  isHorizontal,
  isStartCorner
}) => {
  if (extAmount === 0) return 0;
  if (parallelZ > 0) {
    if (parallelZ > thisZ) {
      return extAmount;
    } else if (parallelZ === thisZ) {
      if (perpZ > thisZ) {
        return extAmount;
      }
      return isStartCorner ? -extAmount : extAmount;
    }
  }
  if (thisZ > perpZ) {
    return -extAmount;
  } else if (thisZ < perpZ) {
    return extAmount;
  } else {
    return isHorizontal ? -extAmount : extAmount;
  }
};
function resolveCandidates(a, b) {
  if (!a && !b) return null;
  if (a && !b) return a;
  if (b && !a) return b;
  const ca = a;
  const cb = b;
  const aTransparent = isTransparent(ca.color);
  const bTransparent = isTransparent(cb.color);
  const aWidth = ca.width ?? 0;
  const bWidth = cb.width ?? 0;
  const aAbsent = aTransparent || aWidth === 0;
  const bAbsent = bTransparent || bWidth === 0;
  if (aTransparent && ca.isExplicit && !cb.isExplicit) return ca;
  if (bTransparent && cb.isExplicit && !ca.isExplicit) return cb;
  if (aAbsent && !bAbsent) return cb;
  if (!aAbsent && bAbsent) return ca;
  if (aAbsent && bAbsent) return null;
  if (aWidth !== bWidth) return aWidth > bWidth ? ca : cb;
  if (ca.isExplicit && !cb.isExplicit) return ca;
  if (!ca.isExplicit && cb.isExplicit) return cb;
  if (ca.spanSpecificity !== cb.spanSpecificity) {
    return ca.spanSpecificity < cb.spanSpecificity ? ca : cb;
  }
  const aPref = ca.side === "top" || ca.side === "left";
  const bPref = cb.side === "top" || cb.side === "left";
  if (aPref !== bPref) return aPref ? ca : cb;
  return ca.cellRef < cb.cellRef ? ca : cb;
}
function buildCellGrid(tableNode) {
  const rows = tableNode.nodes || [];
  const height = rows.length;
  if (height === 0) return { cells: [], width: 0, height: 0, cellAt: () => null };
  const firstRow = rows[0];
  const firstRowCells = firstRow.nodes || [];
  let width = 0;
  for (const cell of firstRowCells) {
    width += cell.tableCellData?.colspan ?? 1;
  }
  const cells = [];
  const grid = [];
  for (let r = 0; r < height; r++) {
    grid[r] = new Array(width).fill(null);
  }
  let actualRow = 0;
  for (const rowNode of rows) {
    if (!rowNode.nodes || rowNode.nodes.length === 0) {
      actualRow++;
      continue;
    }
    let actualCol = 0;
    for (const cellNode of rowNode.nodes) {
      while (actualCol < width && grid[actualRow][actualCol] !== null) {
        actualCol++;
      }
      if (actualCol >= width) break;
      const colspan = cellNode.tableCellData?.colspan ?? 1;
      const rowspan = cellNode.tableCellData?.rowspan ?? 1;
      const cellInfo = {
        ref: cellNode.id || `${actualRow}-${actualCol}`,
        row: actualRow,
        col: actualCol,
        rowspan,
        colspan,
        colors: cellNode.tableCellData?.borderColors || {},
        widths: cellNode.tableCellData?.borderWidths || {}
      };
      cells.push(cellInfo);
      for (let r = 0; r < rowspan && actualRow + r < height; r++) {
        for (let c = 0; c < colspan && actualCol + c < width; c++) {
          grid[actualRow + r][actualCol + c] = cellInfo;
        }
      }
      actualCol += colspan;
    }
    actualRow++;
  }
  const cellAt = (row, col) => {
    if (row < 0 || row >= height || col < 0 || col >= width) return null;
    return grid[row][col];
  };
  return { cells, width, height, cellAt };
}
function createCandidate(cell, side, drawDefaultGrid, isHorizontal) {
  const colorKey = side;
  const widthKey = side;
  const color = cell.colors[colorKey] ?? (drawDefaultGrid ? DEFAULT_COLOR : "");
  const width = cell.widths[widthKey] ?? (drawDefaultGrid ? DEFAULT_WIDTH : 0);
  const spanSpecificity = isHorizontal ? cell.colspan : cell.rowspan;
  return {
    cellRef: cell.ref,
    side,
    color,
    width,
    spanSpecificity,
    isExplicit: cell.colors[colorKey] !== void 0 || cell.widths[widthKey] !== void 0,
    zIndex: calculateZIndex(width, color, isHorizontal)
  };
}
function resolveHorizontalEdge(above, below, drawDefaultGrid) {
  const candA = above ? createCandidate(above, "bottom", drawDefaultGrid, true) : null;
  const candB = below ? createCandidate(below, "top", drawDefaultGrid, true) : null;
  return resolveCandidates(candA, candB);
}
function resolveVerticalEdge(left, right, drawDefaultGrid) {
  const candL = left ? createCandidate(left, "right", drawDefaultGrid, false) : null;
  const candR = right ? createCandidate(right, "left", drawDefaultGrid, false) : null;
  return resolveCandidates(candL, candR);
}
function calculateZIndex(width, color, isHorizontal) {
  const isExplicit = color !== DEFAULT_COLOR;
  return Math.round(width * 100) + (isExplicit ? 50 : 0) + (isHorizontal ? 1 : 0);
}
function makeBorder({
  color,
  width,
  isHorizontal,
  extendStart,
  extendEnd,
  perpStartZ,
  perpEndZ,
  parallelStartZ,
  parallelEndZ
}) {
  if (width <= 0 || isTransparent(color)) return null;
  return {
    color,
    width,
    zIndex: calculateZIndex(width, color, isHorizontal),
    extendStart,
    extendEnd,
    perpStartZ,
    perpEndZ,
    parallelStartZ,
    parallelEndZ
  };
}
function resolveCellBordersSpacingMode(cells, drawDefaultGrid) {
  const result = /* @__PURE__ */ new Map();
  const def = drawDefaultGrid;
  cells.forEach((cell) => {
    const w = {
      top: cell.widths.top ?? (def ? DEFAULT_WIDTH : 0),
      bottom: cell.widths.bottom ?? (def ? DEFAULT_WIDTH : 0),
      left: cell.widths.left ?? (def ? DEFAULT_WIDTH : 0),
      right: cell.widths.right ?? (def ? DEFAULT_WIDTH : 0)
    };
    const c = {
      top: cell.colors.top ?? (def ? DEFAULT_COLOR : ""),
      bottom: cell.colors.bottom ?? (def ? DEFAULT_COLOR : ""),
      left: cell.colors.left ?? (def ? DEFAULT_COLOR : ""),
      right: cell.colors.right ?? (def ? DEFAULT_COLOR : "")
    };
    const leftZ = calculateZIndex(w.left, c.left, false);
    const rightZ = calculateZIndex(w.right, c.right, false);
    const topZ = calculateZIndex(w.top, c.top, true);
    const bottomZ = calculateZIndex(w.bottom, c.bottom, true);
    result.set(cell.ref, {
      top: makeBorder({
        color: c.top,
        width: w.top,
        isHorizontal: true,
        extendStart: w.left / 2,
        extendEnd: w.right / 2,
        perpStartZ: leftZ,
        perpEndZ: rightZ,
        parallelStartZ: 0,
        parallelEndZ: 0
      }),
      bottom: makeBorder({
        color: c.bottom,
        width: w.bottom,
        isHorizontal: true,
        extendStart: w.left / 2,
        extendEnd: w.right / 2,
        perpStartZ: leftZ,
        perpEndZ: rightZ,
        parallelStartZ: 0,
        parallelEndZ: 0
      }),
      left: makeBorder({
        color: c.left,
        width: w.left,
        isHorizontal: false,
        extendStart: w.top / 2,
        extendEnd: w.bottom / 2,
        perpStartZ: topZ,
        perpEndZ: bottomZ,
        parallelStartZ: 0,
        parallelEndZ: 0
      }),
      right: makeBorder({
        color: c.right,
        width: w.right,
        isHorizontal: false,
        extendStart: w.top / 2,
        extendEnd: w.bottom / 2,
        perpStartZ: topZ,
        perpEndZ: bottomZ,
        parallelStartZ: 0,
        parallelEndZ: 0
      })
    });
  });
  return result;
}
function resolveCellBorders(tableNode, options = {}) {
  const { drawDefaultGrid = true, cellSpacing = 0 } = options;
  const { cells, width: W, height: H, cellAt } = buildCellGrid(tableNode);
  if (cellSpacing > 0) {
    return resolveCellBordersSpacingMode(cells, drawDefaultGrid);
  }
  const result = /* @__PURE__ */ new Map();
  for (const cell of cells) {
    result.set(cell.ref, { top: null, right: null, bottom: null, left: null });
  }
  const horizontalEdges = [];
  const verticalEdges = [];
  for (let er = 0; er <= H; er++) {
    horizontalEdges[er] = new Array(W).fill(null);
  }
  for (let r = 0; r < H; r++) {
    verticalEdges[r] = new Array(W + 1).fill(null);
  }
  for (let er = 0; er <= H; er++) {
    for (let c = 0; c < W; c++) {
      const above = er > 0 ? cellAt(er - 1, c) : null;
      const below = er < H ? cellAt(er, c) : null;
      if (above && below && above.ref === below.ref) continue;
      const winner = resolveHorizontalEdge(above, below, drawDefaultGrid);
      horizontalEdges[er][c] = winner;
    }
  }
  for (let r = 0; r < H; r++) {
    for (let ec = 0; ec <= W; ec++) {
      const left = ec > 0 ? cellAt(r, ec - 1) : null;
      const right = ec < W ? cellAt(r, ec) : null;
      if (left && right && left.ref === right.ref) continue;
      const winner = resolveVerticalEdge(left, right, drawDefaultGrid);
      verticalEdges[r][ec] = winner;
    }
  }
  const getVerticalBorderExtension = (edgeRow, edgeCol) => {
    const above = edgeRow > 0 ? verticalEdges[edgeRow - 1]?.[edgeCol] : null;
    const below = edgeRow < H ? verticalEdges[edgeRow]?.[edgeCol] : null;
    const aboveWidth = above && !isTransparent(above.color) ? above.width : 0;
    const belowWidth = below && !isTransparent(below.color) ? below.width : 0;
    return Math.max(aboveWidth, belowWidth) / 2;
  };
  const getHorizontalBorderExtension = (edgeRow, edgeCol) => {
    const left = edgeCol > 0 ? horizontalEdges[edgeRow]?.[edgeCol - 1] : null;
    const right = edgeCol < W ? horizontalEdges[edgeRow]?.[edgeCol] : null;
    const leftWidth = left && !isTransparent(left.color) ? left.width : 0;
    const rightWidth = right && !isTransparent(right.color) ? right.width : 0;
    return Math.max(leftWidth, rightWidth) / 2;
  };
  const getVerticalMaxZ = (edgeRow, edgeCol) => {
    const vAbove = edgeRow > 0 ? verticalEdges[edgeRow - 1]?.[edgeCol] : null;
    const vBelow = edgeRow < H ? verticalEdges[edgeRow]?.[edgeCol] : null;
    const vAboveZ = vAbove && !isTransparent(vAbove.color) && vAbove.width > 0 ? vAbove.zIndex : 0;
    const vBelowZ = vBelow && !isTransparent(vBelow.color) && vBelow.width > 0 ? vBelow.zIndex : 0;
    return Math.max(vAboveZ, vBelowZ);
  };
  const getHorizontalMaxZ = (edgeRow, edgeCol) => {
    const hLeft = edgeCol > 0 ? horizontalEdges[edgeRow]?.[edgeCol - 1] : null;
    const hRight = edgeCol < W ? horizontalEdges[edgeRow]?.[edgeCol] : null;
    const hLeftZ = hLeft && !isTransparent(hLeft.color) && hLeft.width > 0 ? hLeft.zIndex : 0;
    const hRightZ = hRight && !isTransparent(hRight.color) && hRight.width > 0 ? hRight.zIndex : 0;
    return Math.max(hLeftZ, hRightZ);
  };
  const getAdjacentHorizontalZ = (row, col, atLeftCorner) => {
    let adjacent = null;
    if (atLeftCorner) {
      adjacent = col > 0 ? horizontalEdges[row]?.[col - 1] : null;
    } else {
      adjacent = col < W ? horizontalEdges[row]?.[col] : null;
    }
    return adjacent && !isTransparent(adjacent.color) && adjacent.width > 0 ? adjacent.zIndex : 0;
  };
  const getAdjacentVerticalZ = (row, col, atTopCorner) => {
    let adjacent = null;
    if (atTopCorner) {
      adjacent = row > 0 ? verticalEdges[row - 1]?.[col] : null;
    } else {
      adjacent = row < H ? verticalEdges[row]?.[col] : null;
    }
    return adjacent && !isTransparent(adjacent.color) && adjacent.width > 0 ? adjacent.zIndex : 0;
  };
  const findHorizontalEdgeWinner = (edgeRow, startCol, colSpan, cellRef) => {
    for (let c = startCol; c < startCol + colSpan; c++) {
      const edge = horizontalEdges[edgeRow]?.[c];
      if (edge && edge.cellRef === cellRef && !isTransparent(edge.color) && edge.width > 0) {
        return edge;
      }
    }
    return null;
  };
  const findVerticalEdgeWinner = (edgeCol, startRow, rowSpan, cellRef) => {
    for (let r = startRow; r < startRow + rowSpan; r++) {
      const edge = verticalEdges[r]?.[edgeCol];
      if (edge && edge.cellRef === cellRef && !isTransparent(edge.color) && edge.width > 0) {
        return edge;
      }
    }
    return null;
  };
  for (const cell of cells) {
    const borders = result.get(cell.ref);
    const { row, col, rowspan, colspan } = cell;
    const topEdge = findHorizontalEdgeWinner(row, col, colspan, cell.ref);
    if (topEdge) {
      borders.top = makeBorder({
        color: topEdge.color,
        width: topEdge.width,
        isHorizontal: true,
        extendStart: getVerticalBorderExtension(row, col),
        extendEnd: getVerticalBorderExtension(row, col + colspan),
        perpStartZ: getVerticalMaxZ(row, col),
        perpEndZ: getVerticalMaxZ(row, col + colspan),
        parallelStartZ: getAdjacentHorizontalZ(row, col, true),
        parallelEndZ: getAdjacentHorizontalZ(row, col + colspan, false)
      });
    }
    const bottomEdge = findHorizontalEdgeWinner(row + rowspan, col, colspan, cell.ref);
    if (bottomEdge) {
      borders.bottom = makeBorder({
        color: bottomEdge.color,
        width: bottomEdge.width,
        isHorizontal: true,
        extendStart: getVerticalBorderExtension(row + rowspan, col),
        extendEnd: getVerticalBorderExtension(row + rowspan, col + colspan),
        perpStartZ: getVerticalMaxZ(row + rowspan, col),
        perpEndZ: getVerticalMaxZ(row + rowspan, col + colspan),
        parallelStartZ: getAdjacentHorizontalZ(row + rowspan, col, true),
        parallelEndZ: getAdjacentHorizontalZ(row + rowspan, col + colspan, false)
      });
    }
    const leftEdge = findVerticalEdgeWinner(col, row, rowspan, cell.ref);
    if (leftEdge) {
      borders.left = makeBorder({
        color: leftEdge.color,
        width: leftEdge.width,
        isHorizontal: false,
        extendStart: getHorizontalBorderExtension(row, col),
        extendEnd: getHorizontalBorderExtension(row + rowspan, col),
        perpStartZ: getHorizontalMaxZ(row, col),
        perpEndZ: getHorizontalMaxZ(row + rowspan, col),
        parallelStartZ: getAdjacentVerticalZ(row, col, true),
        parallelEndZ: getAdjacentVerticalZ(row + rowspan, col, false)
      });
    }
    const rightEdge = findVerticalEdgeWinner(col + colspan, row, rowspan, cell.ref);
    if (rightEdge) {
      borders.right = makeBorder({
        color: rightEdge.color,
        width: rightEdge.width,
        isHorizontal: false,
        extendStart: getHorizontalBorderExtension(row, col + colspan),
        extendEnd: getHorizontalBorderExtension(row + rowspan, col + colspan),
        perpStartZ: getHorizontalMaxZ(row, col + colspan),
        perpEndZ: getHorizontalMaxZ(row + rowspan, col + colspan),
        parallelStartZ: getAdjacentVerticalZ(row, col + colspan, true),
        parallelEndZ: getAdjacentVerticalZ(row + rowspan, col + colspan, false)
      });
    }
  }
  return result;
}
function calculateBorderPadding(tableNode) {
  let maxStart = 1, maxEnd = 1, maxTop = 1, maxBottom = 1;
  if (tableNode.nodes) {
    const rows = tableNode.nodes;
    const firstRow = rows[0];
    const lastRow = rows[rows.length - 1];
    if (firstRow?.nodes) {
      for (const cell of firstRow.nodes) {
        maxTop = Math.max(maxTop, cell.tableCellData?.borderWidths?.top ?? 1);
      }
    }
    if (lastRow?.nodes) {
      for (const cell of lastRow.nodes) {
        maxBottom = Math.max(maxBottom, cell.tableCellData?.borderWidths?.bottom ?? 1);
      }
    }
    for (const row of rows) {
      if (!row.nodes) continue;
      const firstCell = row.nodes[0];
      const lastCell = row.nodes[row.nodes.length - 1];
      if (firstCell)
        maxStart = Math.max(maxStart, firstCell.tableCellData?.borderWidths?.left ?? 1);
      if (lastCell) maxEnd = Math.max(maxEnd, lastCell.tableCellData?.borderWidths?.right ?? 1);
    }
  }
  return {
    start: Math.ceil(maxStart / 2),
    end: Math.ceil(maxEnd / 2),
    top: Math.ceil(maxTop / 2),
    bottom: Math.ceil(maxBottom / 2)
  };
}
function buildCellGridPm(tableNode) {
  const height = tableNode.content.childCount;
  if (height === 0) return { cells: [], width: 0, height: 0, cellAt: () => null };
  const firstRow = tableNode.content.child(0);
  let width = 0;
  firstRow.content.forEach((cellNode) => {
    width += cellNode.attrs.colspan || 1;
  });
  const cells = [];
  const grid = [];
  for (let r = 0; r < height; r++) {
    grid[r] = new Array(width).fill(null);
  }
  let actualRow = 0;
  tableNode.content.forEach((rowNode) => {
    let actualCol = 0;
    rowNode.content.forEach((cellNode) => {
      while (actualCol < width && grid[actualRow][actualCol] !== null) actualCol++;
      if (actualCol >= width) return;
      const colspan = cellNode.attrs.colspan || 1;
      const rowspan = cellNode.attrs.rowspan || 1;
      const cellInfo = {
        ref: cellNode.attrs.id || `${actualRow}-${actualCol}`,
        row: actualRow,
        col: actualCol,
        rowspan,
        colspan,
        colors: cellNode.attrs.borderColors || {},
        widths: cellNode.attrs.borderWidths || {}
      };
      cells.push(cellInfo);
      for (let r = 0; r < rowspan && actualRow + r < height; r++) {
        for (let c = 0; c < colspan && actualCol + c < width; c++) {
          grid[actualRow + r][actualCol + c] = cellInfo;
        }
      }
      actualCol += colspan;
    });
    actualRow++;
  });
  const cellAt = (row, col) => {
    if (row < 0 || row >= height || col < 0 || col >= width) return null;
    return grid[row][col];
  };
  return { cells, width, height, cellAt };
}
function resolveCellBordersPm(tableNode, options = {}) {
  const { drawDefaultGrid = true, cellSpacing = 0 } = options;
  const { cells, width: W, height: H, cellAt } = buildCellGridPm(tableNode);
  if (cellSpacing > 0) {
    return resolveCellBordersSpacingMode(cells, drawDefaultGrid);
  }
  const result = /* @__PURE__ */ new Map();
  for (const cell of cells) {
    result.set(cell.ref, { top: null, right: null, bottom: null, left: null });
  }
  const horizontalEdges = [];
  const verticalEdges = [];
  for (let er = 0; er <= H; er++) {
    horizontalEdges[er] = new Array(W).fill(null);
  }
  for (let r = 0; r < H; r++) {
    verticalEdges[r] = new Array(W + 1).fill(null);
  }
  for (let er = 0; er <= H; er++) {
    for (let c = 0; c < W; c++) {
      const above = er > 0 ? cellAt(er - 1, c) : null;
      const below = er < H ? cellAt(er, c) : null;
      if (above && below && above.ref === below.ref) continue;
      horizontalEdges[er][c] = resolveHorizontalEdge(above, below, drawDefaultGrid);
    }
  }
  for (let r = 0; r < H; r++) {
    for (let ec = 0; ec <= W; ec++) {
      const left = ec > 0 ? cellAt(r, ec - 1) : null;
      const right = ec < W ? cellAt(r, ec) : null;
      if (left && right && left.ref === right.ref) continue;
      verticalEdges[r][ec] = resolveVerticalEdge(left, right, drawDefaultGrid);
    }
  }
  const getVerticalBorderExtension = (edgeRow, edgeCol) => {
    const above = edgeRow > 0 ? verticalEdges[edgeRow - 1]?.[edgeCol] : null;
    const below = edgeRow < H ? verticalEdges[edgeRow]?.[edgeCol] : null;
    const aboveWidth = above && !isTransparent(above.color) ? above.width : 0;
    const belowWidth = below && !isTransparent(below.color) ? below.width : 0;
    return Math.max(aboveWidth, belowWidth) / 2;
  };
  const getHorizontalBorderExtension = (edgeRow, edgeCol) => {
    const left = edgeCol > 0 ? horizontalEdges[edgeRow]?.[edgeCol - 1] : null;
    const right = edgeCol < W ? horizontalEdges[edgeRow]?.[edgeCol] : null;
    const leftWidth = left && !isTransparent(left.color) ? left.width : 0;
    const rightWidth = right && !isTransparent(right.color) ? right.width : 0;
    return Math.max(leftWidth, rightWidth) / 2;
  };
  const getVerticalMaxZ = (edgeRow, edgeCol) => {
    const vAbove = edgeRow > 0 ? verticalEdges[edgeRow - 1]?.[edgeCol] : null;
    const vBelow = edgeRow < H ? verticalEdges[edgeRow]?.[edgeCol] : null;
    const vAboveZ = vAbove && !isTransparent(vAbove.color) && vAbove.width > 0 ? vAbove.zIndex : 0;
    const vBelowZ = vBelow && !isTransparent(vBelow.color) && vBelow.width > 0 ? vBelow.zIndex : 0;
    return Math.max(vAboveZ, vBelowZ);
  };
  const getHorizontalMaxZ = (edgeRow, edgeCol) => {
    const hLeft = edgeCol > 0 ? horizontalEdges[edgeRow]?.[edgeCol - 1] : null;
    const hRight = edgeCol < W ? horizontalEdges[edgeRow]?.[edgeCol] : null;
    const hLeftZ = hLeft && !isTransparent(hLeft.color) && hLeft.width > 0 ? hLeft.zIndex : 0;
    const hRightZ = hRight && !isTransparent(hRight.color) && hRight.width > 0 ? hRight.zIndex : 0;
    return Math.max(hLeftZ, hRightZ);
  };
  const getAdjacentHorizontalZ = (row, col, atLeftCorner) => {
    let adjacent = null;
    if (atLeftCorner) {
      adjacent = col > 0 ? horizontalEdges[row]?.[col - 1] : null;
    } else {
      adjacent = col < W ? horizontalEdges[row]?.[col] : null;
    }
    return adjacent && !isTransparent(adjacent.color) && adjacent.width > 0 ? adjacent.zIndex : 0;
  };
  const getAdjacentVerticalZ = (row, col, atTopCorner) => {
    let adjacent = null;
    if (atTopCorner) {
      adjacent = row > 0 ? verticalEdges[row - 1]?.[col] : null;
    } else {
      adjacent = row < H ? verticalEdges[row]?.[col] : null;
    }
    return adjacent && !isTransparent(adjacent.color) && adjacent.width > 0 ? adjacent.zIndex : 0;
  };
  const findHorizontalEdgeWinner = (edgeRow, startCol, colSpan, cellRef) => {
    for (let c = startCol; c < startCol + colSpan; c++) {
      const edge = horizontalEdges[edgeRow]?.[c];
      if (edge && edge.cellRef === cellRef && !isTransparent(edge.color) && edge.width > 0) {
        return edge;
      }
    }
    return null;
  };
  const findVerticalEdgeWinner = (edgeCol, startRow, rowSpan, cellRef) => {
    for (let r = startRow; r < startRow + rowSpan; r++) {
      const edge = verticalEdges[r]?.[edgeCol];
      if (edge && edge.cellRef === cellRef && !isTransparent(edge.color) && edge.width > 0) {
        return edge;
      }
    }
    return null;
  };
  for (const cell of cells) {
    const borders = result.get(cell.ref);
    const { row, col, rowspan, colspan } = cell;
    const topEdge = findHorizontalEdgeWinner(row, col, colspan, cell.ref);
    if (topEdge) {
      borders.top = makeBorder({
        color: topEdge.color,
        width: topEdge.width,
        isHorizontal: true,
        extendStart: getVerticalBorderExtension(row, col),
        extendEnd: getVerticalBorderExtension(row, col + colspan),
        perpStartZ: getVerticalMaxZ(row, col),
        perpEndZ: getVerticalMaxZ(row, col + colspan),
        parallelStartZ: getAdjacentHorizontalZ(row, col, true),
        parallelEndZ: getAdjacentHorizontalZ(row, col + colspan, false)
      });
    }
    const bottomEdge = findHorizontalEdgeWinner(row + rowspan, col, colspan, cell.ref);
    if (bottomEdge) {
      borders.bottom = makeBorder({
        color: bottomEdge.color,
        width: bottomEdge.width,
        isHorizontal: true,
        extendStart: getVerticalBorderExtension(row + rowspan, col),
        extendEnd: getVerticalBorderExtension(row + rowspan, col + colspan),
        perpStartZ: getVerticalMaxZ(row + rowspan, col),
        perpEndZ: getVerticalMaxZ(row + rowspan, col + colspan),
        parallelStartZ: getAdjacentHorizontalZ(row + rowspan, col, true),
        parallelEndZ: getAdjacentHorizontalZ(row + rowspan, col + colspan, false)
      });
    }
    const leftEdge = findVerticalEdgeWinner(col, row, rowspan, cell.ref);
    if (leftEdge) {
      borders.left = makeBorder({
        color: leftEdge.color,
        width: leftEdge.width,
        isHorizontal: false,
        extendStart: getHorizontalBorderExtension(row, col),
        extendEnd: getHorizontalBorderExtension(row + rowspan, col),
        perpStartZ: getHorizontalMaxZ(row, col),
        perpEndZ: getHorizontalMaxZ(row + rowspan, col),
        parallelStartZ: getAdjacentVerticalZ(row, col, true),
        parallelEndZ: getAdjacentVerticalZ(row + rowspan, col, false)
      });
    }
    const rightEdge = findVerticalEdgeWinner(col + colspan, row, rowspan, cell.ref);
    if (rightEdge) {
      borders.right = makeBorder({
        color: rightEdge.color,
        width: rightEdge.width,
        isHorizontal: false,
        extendStart: getHorizontalBorderExtension(row, col + colspan),
        extendEnd: getHorizontalBorderExtension(row + rowspan, col + colspan),
        perpStartZ: getHorizontalMaxZ(row, col + colspan),
        perpEndZ: getHorizontalMaxZ(row + rowspan, col + colspan),
        parallelStartZ: getAdjacentVerticalZ(row, col + colspan, true),
        parallelEndZ: getAdjacentVerticalZ(row + rowspan, col + colspan, false)
      });
    }
  }
  return result;
}
export {
  calculateBorderPadding,
  getCornerOffset,
  resolveCellBorders,
  resolveCellBordersPm
};
//# sourceMappingURL=resolve-cell-borders.js.map
